<template>
  <div v-for="(option, i) in normalizedOptions"
    :key="option.value"
    class="form-check"
    role="presentation"
  >
    <label :class="[
      'radio-label form-check-label',
      { 'radio-label-disabled': readonly || $attrs.disabled },
      { 'radio-label-selected': model === option.value }
    ]">
      <input
        v-bind="$attrs"
        v-model="model"
        type="radio"
        :name="uniqName('radio-buttons')"
        :aria-label="option.label"
        :aria-invalid="v$.$error ? 'true' : 'false'"
        :aria-required="required"
        autocomplete="off"
        :class="['form-check-input', { 'is-invalid': v$.$error }]"
        :disabled="$attrs.disabled || (readonly && model !== option.value)"
        :value="option.value"
        @focus="onFocus(i)"
        @blur="onBlur"
        ref="option"
      >
      {{ option.label }}
    </label>
  </div>
</template>

<script>
/* eslint-disable import/extensions */
import { computed } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import focusable from '@ssi/vue-common/src/mixins/focusable';
import { defaultSchema } from '@ssi/vue-common/src/directives/focus';
import { hasProps } from '../../../helpers/util';
import { requiredIf } from '../../../validators';

const { prop: focusProp } = defaultSchema;

export default {
  name: 'RadioButtons',

  setup(props) {
    return {
      v$: useVuelidate(
        computed(() => ({
          modelValue: { required: requiredIf(() => props.required) }
        })),
        computed(() => ({ modelValue: props.modelValue }))
      )
    };
  },

  mixins: [focusable],

  props: {
    label: {
      type: String
    },

    options: {
      type: Array,
      required: true
    },

    required: {
      type: Boolean,
      default: () => false
    },

    readonly: {
      type: Boolean,
      default: () => false
    },

    modelValue: [Boolean, String]
  },

  emits: ['update:modelValue'],

  data: () => ({
    focusedOptionIndex: -1
  }),

  computed: {
    model: {
      get: ({ modelValue }) => modelValue,
      set(newValue) {
        this.$emit('update:modelValue', newValue);
      }
    },

    normalizedOptions: ({ options }) => options.map(o => (hasProps(o, ['value', 'label'])
      ? o
      : {
        value: o,
        label: o !== null ? String(o) : ''
      })),

    selectedOptionIndex: ({ model, options }) =>
      options.findIndex(o => model === o.value)
  },

  methods: {
    onBlur() {
      this.focusedOptionIndex = -1;
      this.hasFocus = false;
      this.v$.$touch();
    },

    onFocus(i) {
      this.focusedOptionIndex = i;
      this.hasFocus = true;
    }
  },

  watch: {
    [focusProp](val) {
      if (val && this.focusedOptionIndex === -1) {
        this.$refs.option[(this.selectedOptionIndex || 0)]?.focus();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "../../../styles/variables";

.radio-label {
  border-radius: 0.3rem;
  cursor: pointer;
  margin-left: -2.25em;
  padding: 0.25em 1em 0.25em 2.25em;
  transition: background-color 0.15s ease-in-out;

  &:hover:not(.radio-label-disabled) {
    background-color: $primary-light;
  }

  &.radio-label-selected {
    background-color: $primary-lighter;
  }
}
</style>
